import AppApi from "./AppApi";
import AppStore from "../stores/Store";
import {JwtToken, RefreshToken} from "../stores/AuthStore";
import {AxiosError} from "axios";
import {VerifyCodeTypes} from "../pages/AuthPage/types";
import { ROUTES } from "../constants/routes";

export enum UserRole {
  Athlete,
  Coach,
}

type UserSaveRequest = {
  fullName: string;
  email: string;
  password: string;
  consent: boolean;
  role: UserRole | null;
};

type VerifyCodeRequest = {
  token: string;
  code: string;
  type?: VerifyCodeTypes;
};

export default class AuthApi {
  constructor(private api: AppApi, private store: AppStore) {
    this.api = api;
    this.store = store;
  }

  async auth({
    login,
    password,
    rememberMe,
  }: {
    login: string;
    password: string;
    rememberMe: boolean;
  }) {
    const response = await this.api.client.post("/auth/api/v1/auth", {
      email: login,
      pwd: password,
      rememberMe: rememberMe,
    });

    const data = response.data;
    if (response.status === 200) {
      this.store.authStore.saveAuthToken(data.accessToken, data.refreshToken);
      await this.getUserInfo();
      return { success: true };
    } else {
      return { success: false, errors: [response.data.title] };
    }
  }

  async refresh() {
    const accessToken = localStorage.getItem(JwtToken);
    const client = this.api.clientWithoutInterceptors;
    client.defaults.headers.common["Authorization"] = "Bearer " + accessToken;

    try {
      const response = await client.post("/auth/api/v1/auth/refresh", {
        refreshToken: localStorage.getItem(RefreshToken),
        accessToken: accessToken,
      });

      this.store.authStore.saveAuthToken(
        response.data.accessToken,
        response.data.refreshToken
      );
      return true;
    } catch {
      return false;
    }
  }

  async register(item: UserSaveRequest) {
    const response = await this.api.client.post(
      "/auth/api/v1/auth/register",
      item
    );

    if (response.status === 200) {
      this.store.authStore.setVerificationToken(response.data);
      return { success: true };
    } else {
      return { success: false, errors: ["ошибка"] };
    }
  }

  async logout() {
    const token = await this.store.authStore.getAuthToken();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await this.api.client.post(
      `/auth/api/v1/logout`,
      {},
      config
    );

    console.log(response);
    this.store.authStore.logout();
  }

  async getResetPasswordCode({
    email,
  }: {
    email: string;
  }) {
    console.log("getResetPasswordToken");
    const response = await this.api.client.post(
      "/auth/api/v1/password/reset/code",
      {
        email: email
      }
    );

    if (response.status === 200) {
      this.store.authStore.setVerificationToken(response.data);
      return { success: true };
    } else {
      return { success: false, errors: ["ошибка"] };
    }
  }

  async resetPassword({
    pwd,
    token,
  }: {
    pwd: string;
    token: string;
  }) {
    const response = await this.api.client.post("/auth/api/v1/password/reset", {
      pwd: pwd,
      token: atob(token),
    });

    if (response.status === 200) {
      return { success: true };
    } else {
      return { success: false, errors: ["ошибка"] };
    }
  }

  async changePassword({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }) {
    const response = await this.api.client.post(
      "/auth/api/v1/password/change",
      {
        oldPassword: oldPassword,
        newPassword: newPassword,
      }
    );

    if (response.status === 200) {
      return { success: true };
    } else {
      return { success: false, errors: ["ошибка"] };
    }
  }

  async getUserInfo() {
    try {
      this.api.workoutApi.initLoadMyWorkouts();
      const response = await this.api.client.get("/users/api/v1/users/current");
      if (response.status === 200) {
        this.store.authStore.saveCurrentUser(response.data);
        this.store.profileStore.setActiveSubscriptions(response.data.activeSubscriptions);
        this.store.profileStore.setIsCoach(response.data.isCoach);
      } else {
        this.store.authStore.logout();
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status === 401) {
          this.store.authStore.saveCurrentUser(undefined);
          window.location.href = ROUTES.LOGIN + ROUTES.REGISTER;
          return;
        }
      } else {
        console.error(e);
      }
    }
  }

  async verifyCode(item: VerifyCodeRequest) {
    try {
      const response = await this.api.client.post(
        "/auth/api/v1/verify",
        item
      );
      
      const data = response.data;

      if (response.status === 200) {
        this.store.authStore.setVerified(true);
        if (item.type === VerifyCodeTypes.registration) {
          this.store.authStore.saveAuthToken(data.accessToken, data.refreshToken);
        }
        return {success: true};
      } else {
        return {success: false, errors: ["ошибка"]};
      }
    }
    catch (e: any) {
      return {success: false, errors: e.response.data};
    }
  }

  async resendVerifyCode() {
    const token = this.store.authStore.getVerificationToken();
    if (!token) return;

    const response = await this.api.client.get(`/auth/api/v1/verify/resend?token=${token.token}`);

    if (response.status === 200) {
      this.store.authStore.setVerified(false);
      return { success: true };
    } else {
      return { success: false, errors: ["ошибка"] };
    }
  }
  
  async getShortToken() {
    try {
      const response = await this.api.client.get(`/auth/api/v1/short-token`);
      
      if (response.status === 200) {
        return {data: response.data};
      } else {
        return {errors: ["ошибка"]};
      }
    }
    catch (e) {
      return {errors: ["ошибка получения токена"]};
    }
  }
  
  async verifyShortToken(shortToken: string) {
    try {
      const response = await this.api.client.get(`/auth/api/v1/short-token/verify?token=${encodeURIComponent(shortToken)}`);
      
      if (response.status === 200) {
        return {data: response.data};
      }
      else if (response.status === 204) {
        return {errors: ["Ой, ваша сессия устарела! Для безопасности ваших данных, пожалуйста, авторизуйтесь заново."]};
      }
      else {
        return {errors: ["ошибка"]};
      }
    }
    catch (e) {
      return {errors: ["ошибка проверки токена"]};
    }
  }
}
